import React from 'react'
import { Helmet } from 'react-helmet'

import Layout from '../components/Layout'
import {
  Text,
  Link,
  Box,
  HR,
  Row,
  Column,
  Circle,
  Heading
} from '../components/styled'
import SectionBanner from '../components/SectionBanner'
import Marquee from '../components/Marquee'
import Carousel from '../components/Carousel'

import { colors } from '../components/styled/theme'

// import Sun from '../components/Sun'
import Video from '../components/Video'
import Mtn from '../components/svg/Mtn'

import cloud from '../static/videos/clouds.mp4'
import turbine from '../static/videos/turbine.mp4'

import Design from '../components/svg/Design'
import Engineering from '../components/svg/Engineering'
import Ideation from '../components/svg/Ideation'

import GraphQLIcon from '../components/svg/GraphQL'
import ReactIcon from '../components/svg/React'

import indexStatic from '../static/hero/index-static.png'
import carbonBundle from '../static/carbon-bundle.png'
import plotlyBundle from '../static/plotly-bundle.png'
import jack from '../static/jack.png'
import johannes from '../static/johannes.jpeg'
// import rauchg from '../static/rauchg.jpg'
import rajoshi from '../static/rajoshi.jpeg'

import zeit from '../static/companies/zeit.svg'
import fossa from '../static/companies/fossa.svg'
import plotly from '../static/companies/plotly.svg'
import bitpayper from '../static/companies/bitpayper.svg'

import openSourceLogos from '../static/companies/open-source-logos.svg'

const HeroDynamic = props => (
  <Column position="relative" height="100%" mt="14rem" {...props}>
    <Box
      position="absolute"
      left="3.5rem"
      width="7.25rem"
      height="6rem"
      bottom="19rem"
      border={2}
      css={{
        background: colors.green,
        overflow: 'hidden'
      }}
    >
      <Video
        opacity={0.5}
        width="10.625rem"
        objectPosition="left"
        height="100%"
        src={turbine}
        i={1}
      />
    </Box>
    <Box
      position="absolute"
      left="8.125rem"
      top="-9.75rem"
      width="14.5rem"
      height="6rem"
      border={2}
      css={{
        background: colors.orange,
        overflow: 'hidden'
      }}
    >
      <Video src={cloud} i={1} />
    </Box>
    <Circle
      zIndex={-1}
      position="absolute"
      bg={colors.blue}
      top="-2.75rem"
      left="7rem"
      size="29.25rem"
    />
    <Box position="relative">
      <Mtn />
    </Box>
  </Column>
)

const HeroStatic = props => (
  <Column justifyContent="center" height="100%" {...props}>
    <img width="100%" src={indexStatic} alt="Index page artwork" />
  </Column>
)

const Card = ({ title, description, link, image }) => (
  <Row
    flexDirection={['column', 'row', 'row']}
    width="100%"
    border={2}
    mb={8}
    py={[6, '3.25rem']}
  >
    <Column
      maxWidth={['unset', 'unset', 320]}
      width={['100%', '100%', '80%', '75%']}
      mx={[0, 0, 7]}
      mb={[7, 0, 0]}
      pr={[6, 0, 0]}
      pl={[6, 7, 0]}
      justifyContent="center"
    >
      <Text mb={4} fontWeight="medium" fontSize={6}>
        {title}
      </Text>
      <Text.p mb={[6, 6, 7]}>{description}</Text.p>
      <Box>{link}</Box>
    </Column>
    <Column
      mr={[0, 0, 48]}
      px={[6, 7, 0]}
      width={['unset', '150%', 'unset']}
      justifyContent="center"
    >
      {image}
    </Column>
  </Row>
)

function CapabilityCard({ icon, title, children }) {
  const hasButtons = children.length > 2

  return (
    <Row
      justifyContent={['space-between', 'flex-start']}
      flexDirection={['column', 'row']}
      alignItems={['unset', hasButtons ? 'unset' : 'center']}
      mb={8}
      p={6}
      border={2}
    >
      <Column
        border={2}
        borderColor="gray1"
        alignItems="center"
        justifyContent="center"
        width={['100%', '42%']}
        height="21rem"
        mb={[5, 0]}
        mr={[0, '4rem']}
      >
        {icon}
      </Column>
      <Column width={['100%', '50%']}>
        {title && (
          <Heading.h3
            mb={4}
            mt={[0, hasButtons ? 4 : 0]}
            fontSize={6}
            fontWeight="medium"
          >
            {title}
          </Heading.h3>
        )}
        {children}
      </Column>
    </Row>
  )
}

const IndexPage = props => (
  <Layout location={props.location} px={0}>
    <Column width="100%" alignItems="center" px={['1.5rem', '5rem']}>
      <Helmet>
        <title>Dawn Labs — Thoughtful products for inspired teams</title>
      </Helmet>
      <Row
        width="100%"
        flexDirection={['column-reverse', 'row']}
        pt={['4.5rem', 'unset']}
        pb={[8, 'unset']}
        alignItems={['center', 'unset']}
        justifyContent={['flex-end', 'space-between']}
        height={['unset', '48rem']}
      >
        <Column
          justifyContent="center"
          width={['100%', '50%', '48%', '38%']}
          maxWidth={['unset', '25.25rem', '25.25rem', '27rem']}
        >
          <Heading.h1 mb={6}>Thoughtful products for inspired teams</Heading.h1>
          <Text.subheader>
            With a holistic approach to engineering and design, we partner with
            startups and enterprises to build for the digital era.
          </Text.subheader>
          <Text.subheader mt={6} color="#C8C8C8">
            React · GraphQL · Node · Design
          </Text.subheader>
        </Column>
        <HeroDynamic display={['none', 'none', 'flex']} />
        <HeroStatic
          width={['100%', '45%']}
          maxWidth={[200, 'unset']}
          mb={[7, 0]}
          display={['flex', 'flex', 'none']}
        />
      </Row>
      <Row bg="gray0" width="100vw" justifyContent="center" mb="6rem">
        <Row
          width="100%"
          py={[6, '3rem', 7, '53px']}
          px={['1.5rem', '5rem']}
          maxWidth="85rem"
          justifyContent="space-between"
          alignItems="center"
          flexDirection={['column', 'column', 'row', 'row']}
        >
          <Text.detail
            lineHeight={1}
            fontSize={['12px', 3, 4, 20]}
            fontWeight="medium"
            mb={[5, '2.25rem', 0, 0]}
            css={{
              verticalAlign: 'middle',
              textTransform: 'uppercase',
              letterSpacing: '0.02em'
            }}
          >
            Working With the Best
          </Text.detail>
          <Row
            width={['100%', '95%', '70%', '65%']}
            alignItems="center"
            justifyContent="space-between"
            px={[0, 5, 6, 0]}
            css={{ opacity: 0.5 }}
          >
            <Box
              display="inherit"
              mx={['1rem', '2rem', 0, 0]}
              mb="-2px"
              maxWidth="100%"
            >
              <img src={zeit} width="100%" alt="ZEIT" />
            </Box>
            <Box display="inherit" mx={['1rem', '2rem', 0, 0]} maxWidth="100%">
              <img src={fossa} width="100%" alt="Bitpayer" />
            </Box>
            <Box display="inherit" mx={['1rem', '2rem', 0, 0]} maxWidth="100%">
              <img src={bitpayper} width="100%" alt="Bitpayer" />
            </Box>
            <Box display="inherit" mx={['1rem', '2rem', 0, 0]} maxWidth="100%">
              <img src={plotly} width="100%" alt="Plotly" />
            </Box>
          </Row>
        </Row>
      </Row>
      <Row
        justifyContent={['unset', 'unset', 'space-between']}
        flexDirection={['column', 'column', 'row']}
        mb={[8, 7, 0]}
      >
        <Column width={['100%', '100%', 'unset']}>
          <Text textAlign="center" fontSize={[4, 4, 22, 22]}>
            Let us tackle your
          </Text>
          <Marquee />
        </Column>
        <Text.p
          color="gray2"
          width={['unset', 'unset', '45%']}
          mt={4}
          mr={[0, 0, 7, 7]}
        >
          Whether you need team augmentation, technical consulting, or
          full-service product development, we’re here to create whatever you
          can dream up. We’re small, so we work intimately with you and get to
          know your problems as if they’re our own.
        </Text.p>
      </Row>
      <Carousel
        slides={[
          // {
          //   title: `I’ve been involved in many many launches over the years—and this was the best, by some distance.`,
          //   subtitle: `Guillermo Rauch, Founder & CEO - ZEIT`,
          //   img: rauchg
          // },
          {
            title: `When it comes to best practices and understanding emerging tech, they’re top-tier folks.`,
            subtitle: `Johannes Schickling, CEO - Prisma`,
            img: johannes
          },
          {
            title: `If we need a product designed beautifully and built reliably, we know we can count on them.`,
            subtitle: `Jack Parmer, CEO - Plotly`,
            img: jack
          },
          {
            title: `They are strong engineers, who also have an exceptional eye for design and product.`,
            subtitle: `Rajoshi Ghosh, Co-founder - Hasura`,
            img: rajoshi
          }
        ]}
      />
      <HR />
      <SectionBanner mb={7} title="Capabilities">
        Being an interdisciplinary team, we apply a comprehensive approach to
        every phase of product development.
      </SectionBanner>

      <CapabilityCard icon={<Engineering />} title="Software">
        <Text mb={5} color="gray1" fontSize={3} lineHeight={1.5}>
          Node · UI & UX · TypeScript · React Native · Electron
        </Text>
        <Text.p>
          We cut through the noise and help you leverage the best tools to
          create simple, scalable software. Whether we’re engineering an
          application from the ground up or redesigining an existing interface,
          we bring disciplined, experience-driven methodology to all of our
          projects.
        </Text.p>
        <Row mt="2.5rem" mb={4}>
          <Text mr={5} color="gray3" fontSize={3}>
            <Row
              border={2}
              borderColor="gray0"
              alignItems="center"
              px={5}
              py={4}
            >
              <ReactIcon />
              <Box ml={20}>React</Box>
              {/* mr={5} TODO once the page is ready → */}
            </Row>
          </Text>
          <Text color="gray3" fontSize={3}>
            <Row
              border={2}
              borderColor="gray0"
              alignItems="center"
              px={5}
              py={4}
            >
              <GraphQLIcon />
              <Box ml={20} color="gray3">
                GraphQL
              </Box>
              {/* TODO once the page is ready → */}
            </Row>
          </Text>
        </Row>
      </CapabilityCard>

      <Column
        bg="gray0"
        width="100vw"
        alignItems="center"
        px={[5, 8]}
        py="3.75rem"
        mb={8}
      >
        <Text color="#979797" mb="2.5rem" fontSize={4}>
          Our{' '}
          <Link to="/open-source" fontSize="inherit">
            open source
          </Link>{' '}
          tools have been used at
        </Text>
        <Row width="100%" justifyContent="space-around" alignItems="center">
          <img
            src={openSourceLogos}
            width="100%"
            style={{ maxWidth: '1080px' }}
          />
        </Row>
      </Column>

      <CapabilityCard icon={<Design />} title="Design">
        <Text mb={5} color="gray1" fontSize={3} lineHeight={1.5}>
          UI & UX · Interaction · Product · Design Systems · Visual
        </Text>
        <Text.p>
          Looking to streamline your product’s interface or clarify your brand
          vision? We’ve worked with organizations large and small to overhaul
          their visual presence and better capture their values.
        </Text.p>
      </CapabilityCard>

      <CapabilityCard icon={<Ideation />} title="Research">
        <Text mb={5} color="gray1" fontSize={3} lineHeight={1.5}>
          Prototyping · Concept Development · Product Strategy
        </Text>
        <Text.p>
          Product development is hard. Our team has experience sifting through
          nebulous requirements and getting to the heart of your user’s needs.
          We help you accelerate by providing clarity, driving direction, and
          testing hypotheses.
        </Text.p>
      </CapabilityCard>

      <HR />
      <SectionBanner title="Selected Projects">
        From brand identities to full blown applications, we have experience
        building things for organizations of all sizes.
      </SectionBanner>
      <Card
        title="Plotly"
        description="Expanding the potentials of Plotly’s framework for building
      analytical web applications"
        link={
          <Link.internal to="/case-studies/plotly/dds">
            VIEW CASE STUDY
          </Link.internal>
        }
        image={
          <Box>
            <img width="100%" src={plotlyBundle} alt="Carbon images" />
          </Box>
        }
      />
      <Card
        title="Carbon"
        description="The easiest way to create and share beautiful images of your source code"
        link={
          <Link.internal to="/case-studies/carbon">
            VIEW CASE STUDY
          </Link.internal>
        }
        image={
          <Box>
            <img width="100%" src={carbonBundle} alt="Carbon preview" />
          </Box>
        }
      />
      <Link.internal
        color="gray1"
        mb={6}
        to="/work#Projects"
        fontSize={4}
        fontWeight="medium"
      >
        SEE ALL WORK
      </Link.internal>
    </Column>
  </Layout>
)

export default IndexPage
